import { useEffect, useMemo, useState } from "react";
import { CheckCircle2, XCircle as XCircleIcon, Truck } from "lucide-react";
import Button from "../../../componentes/button/button";
import { capitalizeNome } from "../../../utils/reduzirNome";
import { formatLocalDate, formatLocalDateTime } from "../../../utils/formatDate";
import { Dialog } from "primereact/dialog";
import TabelaRetornosDeEntregas from "./TabelaRetornosDeEntregas";
import DateMasked from "../../../componentes/inputMask/DateMasked";
import { CustomTable } from "../../../componentes/table/CustomTable";
import { retornaTipoDaRota } from "../../../utils/options";
import { EditableSelect, EditableText } from "./EditableCell";
import CargaEntregaService from "../../../services/resource/escalaDeRotaService";
import { toast } from "react-toastify";

const TIPO_ROTA_BADGE = {
    AS: "bg-primary/15 text-primary",
    DOIS_DIAS: "bg-amber-500/15 text-amber-600",
    DIARIA: "bg-success/15 text-success",
    TRES_DIAS: "bg-orange-500/15 text-orange-600",
    REPROGRAMACAO: "bg-destructive/15 text-destructive",
    FRETE: "bg-accent text-accent-foreground",
};

function SyncBadge({ ok }) {
    return ok
        ? <CheckCircle2 className="w-4 h-4 text-success inline" />
        : <XCircleIcon className="w-4 h-4 text-muted-foreground/50 inline" />;
}

function TabelaEscalaDeRotas({
    lista,
    loading = true,
    onEdit,
    onView,
    exportSheet,
    showCreate,
    showDeleteAll,
    veiculos = [],
    motoristas = [],
    auxiliares = [],
    onUpdated,
    dataInicio = null,
    dataFim = null
}) {
    const cargaService = useMemo(() => new CargaEntregaService(), []);

    const [localData, setLocalData] = useState([]);
    const [modalDeRetornos, setModalRetornos] = useState(false);
    const [selectedCarga, setSelectedCarga] = useState([]);
    const [modalExportar, setModalExportar] = useState(false);
    const [modeloExportacao, setModeloExportacao] = useState("SIMPLES");
    const [dataInicioExportacao, setDataInicioExportacao] = useState(dataInicio);
    const [dataFimExportacao, setDataFimExportacao] = useState(dataFim);

    useEffect(() => {
        setLocalData(lista || []);
    }, [lista]);

    const {
        processedData,
    } = useAdvancedTable(localData);

    const conflitos = useMemo(() => computeConflitos(localData), [localData]);

    const aplicarAtualizacao = (dto) => {
        setLocalData(prev => prev.map(item => (item.id === dto.id ? { ...item, ...dto } : item)));
        onUpdated?.(dto);
    };

    const salvarVeiculo = async (row, veiculoId) => {
        try {
            const res = await cargaService.atualizarVeiculo(row.id, veiculoId);
            aplicarAtualizacao(res.data);
        } catch (e) {
            toast.error(e?.response?.data?.message || "Erro ao atualizar veículo");
            throw e;
        }
    };

    const salvarTransporte = async (row, transporte) => {
        try {
            const res = await cargaService.atualizarTransporte(row.id, transporte);
            aplicarAtualizacao(res.data);
        } catch (e) {
            toast.error(e?.response?.data?.message || "Erro ao atualizar transporte");
            throw e;
        }
    };

    const salvarMotorista = async (row, motoristaId) => {
        try {
            const res = await cargaService.atualizarMotorista(row.id, motoristaId);
            aplicarAtualizacao(res.data);
        } catch (e) {
            toast.error(e?.response?.data?.message || "Erro ao atualizar motorista");
            throw e;
        }
    };

    const salvarAuxiliar = async (row, slot, novoId) => {
        const atuais = [row.auxiliares?.[0]?.id ?? null, row.auxiliares?.[1]?.id ?? null];
        atuais[slot] = novoId;
        const novaLista = atuais.filter(Boolean);

        try {
            const res = await cargaService.atualizarAuxiliares(row.id, novaLista);
            aplicarAtualizacao(res.data);
        } catch (e) {
            toast.error(e?.response?.data?.message || "Erro ao atualizar auxiliar");
            throw e;
        }
    };

    const veiculoItemTemplate = (item) => (
        <div className="text-xs">
            {item.placa} <span className="text-muted-foreground">{item.codigoYork} {item.codigoSolar}</span>
        </div>
    );

    const funcionarioItemTemplate = (item) => (
        <div className={`text-xs ${item.ativo === false ? "text-destructive" : ""}`}>
            {item.nome} <span className="text-muted-foreground">{item.codigo}</span>
        </div>
    );

    const columns = useMemo(() => [
        {
            key: "dataOperacao",
            label: "Data",
            sortable: true,
        },
        {
            key: "carga",
            label: "Carga",
            sortable: true,
            render: (_, row) => <span className="font-bold text-foreground hover:bg-gray-50">{row.carga}</span>
        },
        {
            key: "veiculoPlaca",
            label: "Veículo",
            sortable: true,
            noSelected: true,

        },
        {
            key: "cracha",
            label: "Crachá",
            sortable: true
        },
        {
            key: "transporte",
            label: "Transporte",
            sortable: true,
            noSelected: true,
            render: (_, row) => (
                <EditableText
                    value={row.transporte}
                    placeholder="Sem transporte"
                    onSave={(v) => salvarTransporte(row, v)}
                />
            )
        },
        {
            key: "motoristaNome",
            label: "Motorista",
            sortable: true,
            noSelected: true,
            render: (_, row) => {
                const temConflito = row.motoristaId && conflitos.motoristas.has(`${row.dataOperacao}|${row.motoristaId}`);
                return (
                    <EditableSelect
                        value={row.motoristaId}
                        displayLabel={capitalizeNome(row.motoristaNome)}
                        placeholder="Sem motorista"
                        options={motoristas}
                        optionLabel="nome"
                        optionValue="id"
                        itemTemplate={funcionarioItemTemplate}
                        warning={temConflito}
                        warningMessage={`${capitalizeNome(row.motoristaNome)} já está escalado em outra carga neste dia.`}
                        onSave={(v) => salvarMotorista(row, v)}
                    />
                );
            }
        },
        {
            key: "auxiliar1Nome",
            label: "Auxiliar 1",
            noSelected: true,
            render: (_, row) => {
                const auxId = row.auxiliares?.[0]?.id ?? null;
                const temConflito = auxId && conflitos.auxiliares.has(`${row.dataOperacao}|${auxId}`);
                return (
                    <EditableSelect
                        value={auxId}
                        displayLabel={capitalizeNome(row.auxiliar1Nome)}
                        placeholder="Sem auxiliar"
                        options={auxiliares}
                        optionLabel="nome"
                        optionValue="id"
                        itemTemplate={funcionarioItemTemplate}
                        warning={temConflito}
                        warningMessage={`${capitalizeNome(row.auxiliar1Nome)} já está escalado em outra carga neste dia.`}
                        onSave={(v) => salvarAuxiliar(row, 0, v)}
                    />
                );
            }
        },
        {
            key: "auxiliar2Nome",
            label: "Auxiliar 2",
            noSelected: true,
            render: (_, row) => {
                const auxId = row.auxiliares?.[1]?.id ?? null;
                const temConflito = auxId && conflitos.auxiliares.has(`${row.dataOperacao}|${auxId}`);
                return (
                    <EditableSelect
                        value={auxId}
                        displayLabel={capitalizeNome(row.auxiliar2Nome)}
                        placeholder="Sem auxiliar"
                        options={auxiliares}
                        optionLabel="nome"
                        optionValue="id"
                        itemTemplate={funcionarioItemTemplate}
                        warning={temConflito}
                        warningMessage={`${capitalizeNome(row.auxiliar2Nome)} já está escalado em outra carga neste dia.`}
                        onSave={(v) => salvarAuxiliar(row, 1, v)}
                    />
                );
            }
        },
        {
            key: "area",
            label: "Área",
            sortable: true
        },
        {
            key: "tipoRota",
            label: "Tipo Rota",
            sortable: true,
            render: (item) => item ? (
                <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${TIPO_ROTA_BADGE[item] || "bg-accent text-accent-foreground"}`}>
                    {retornaTipoDaRota(item)}
                </span>
            ) : null
        },
        {
            key: "totalEntregas",
            label: "Total Entregas",
            sortable: true,
            center: true
        },
        {
            key: "cubos",
            label: "Cubos (Roterização)",
            sortable: true,
            center: true
        },
        {
            key: "rotaGreenmileTransporte",
            label: "Greenmile Sync",
            sortable: false,
            center: true,
            render: v => <SyncBadge ok={!!v} />
        },
        {
            key: "entregaVolumeTransporte",
            label: "Entrega Volume Sync",
            sortable: false,
            center: true,
            render: v => <SyncBadge ok={!!v} />
        },
        {
            key: "retornos",
            label: "Retornos",
            sortable: false,
            render: (_, row) =>
                row.temRetornos && (
                    <span
                        onClick={() => abrirRetornos(row)}
                        className="underline text-primary cursor-pointer hover:brightness-90 transition-colors duration-150"
                    >
                        Ver Retornos
                    </span>
                )
        },
        {
            key: "entregaVolumeCubos",
            label: "Cubos (SAP)",
            sortable: true,
            center: true
        },
        {
            key: "dataCriacao",
            label: "Data Criação",
            sortable: true,
            render: v => formatLocalDateTime(v)
        },
    ], [veiculos, motoristas, auxiliares, conflitos]);

    const footer = (rows) => {
        return (
            <div className="flex justify-between  items-center text-xs font-semibold px-3">
                <div className="px-3 py-2 flex items-center gap-1.5 text-muted-foreground">
                    <Truck className="w-3.5 h-3.5 text-primary" />
                    <span className="text-foreground">{rows.length}</span> carga{rows.length === 1 ? "" : "s"} nesta seleção
                </div>
                <div className="mr-3">{dataInicio && dataFim && (`Período selecionado: ${formatLocalDate(dataInicio)} até ${formatLocalDate(dataFim)} `)}</div>
            </div>
        );
    };

    const header = <div className="mx-3 flex flex-wrap gap-2 mb-2">
        <Button label="Cadastrar Novo" variant="indigo" iconLeft="fa fa-plus" onClick={() => showCreate()} />
        <Button label="Excluir dia operação" variant="red" iconLeft="fa fa-times" onClick={() => showDeleteAll()} />
        <Button
            label="Exportar Planilha"
            variant="green"
            iconLeft="fa fa-file-excel"
            onClick={() => {
                setDataInicioExportacao(dataInicio);
                setDataFimExportacao(dataFim);
                setModalExportar(true);
            }}
            disabled={lista?.length < 1}
        />
    </div>

    const abrirRetornos = (value) => {
        setSelectedCarga(value);
        setModalRetornos(true);
    }

    const confirmarExportacao = () => {
        exportSheet(modeloExportacao, dataInicioExportacao, dataFimExportacao);
        setModalExportar(false);
    }

    return (
        <div className="rounded-2xl border border-border bg-card shadow-card overflow-hidden">
            <div className="pt-3">
                <CustomTable
                    data={processedData}
                    columns={columns}
                    loading={loading}
                    maxHeight="calc(100vh - 170px)"
                    className="text-[11px]"
                    showHeader={false}
                    onRowSelect={(row) => onEdit(row)}
                    header={header}
                    footer={footer}
                    dense
                />
            </div>

            <Dialog visible={modalDeRetornos}
                onHide={() => setModalRetornos(false)}
                className="w-[95%]"
                contentClassName="p-0"
                blockScroll
                footer={<div className="flex justify-end pt-3">
                    <Button variant="gray" label="Voltar" iconLeft="fa fa-arrow-left" onClick={() => setModalRetornos(false)} />
                </div>}
            >
                <TabelaRetornosDeEntregas values={selectedCarga} />

            </Dialog>

            <Dialog
                header="Exportar Planilha"
                visible={modalExportar}
                onHide={() => setModalExportar(false)}
                className="w-[95%] md:w-[600px]"
                blockScroll
                footer={<div className="flex justify-end gap-2 pt-3">
                    <Button variant="gray" label="Cancelar" iconLeft="fa fa-arrow-left" onClick={() => setModalExportar(false)} />
                    <Button
                        variant="green"
                        label="Exportar"
                        iconLeft="fa fa-file-excel"
                        onClick={confirmarExportacao}
                        disabled={!dataInicioExportacao || !dataFimExportacao}
                    />
                </div>}
            >
                <div className="flex flex-col gap-3 pt-2">
                    <p className="text-sm text-muted-foreground">
                        Escolha o período e o modelo da planilha a ser exportada.
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <DateMasked
                            label="Data inicial"
                            value={dataInicioExportacao}
                            onChange={(v) => setDataInicioExportacao(v)}
                            touched={true}
                        />

                        <DateMasked
                            label="Data final"
                            value={dataFimExportacao}
                            onChange={(v) => setDataFimExportacao(v)}
                            touched={true}
                        />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <button
                            type="button"
                            onClick={() => setModeloExportacao("SIMPLES")}
                            className={`
                            flex items-start gap-3 rounded-2xl border p-4 text-left transition-all duration-150
                            ${modeloExportacao === "SIMPLES"
                                    ? 'border-primary/50 bg-primary/5 shadow-card'
                                    : 'border-border bg-card hover:border-border hover:shadow-card'}
                            `}>
                            <div
                                className={`
                                mt-1 h-4 w-4 rounded-full border-2 flex items-center justify-center shrink-0
                                ${modeloExportacao === "SIMPLES" ? 'border-primary' : 'border-input'}
                                `} >
                                {modeloExportacao === "SIMPLES" && (
                                    <div className="h-2 w-2 rounded-full bg-primary" />
                                )}
                            </div>

                            <div>
                                <div className="text-sm font-medium text-foreground">
                                    Modelo simples
                                </div>

                                <div className="text-xs text-muted-foreground mt-1">
                                    Layout atual: uma linha por carga, com Unidade e Período repetidos em cada linha.
                                </div>
                            </div>
                        </button>

                        <button
                            type="button"
                            onClick={() => setModeloExportacao("SOLAR")}
                            className={`
                            flex items-start gap-3 rounded-2xl border p-4 text-left transition-all duration-150
                            ${modeloExportacao === "SOLAR"
                                    ? 'border-success/50 bg-success/10 shadow-card'
                                    : 'border-border bg-card hover:border-border hover:shadow-card'}
                            `}>
                            <div
                                className={`
                                mt-1 h-4 w-4 rounded-full border-2 flex items-center justify-center shrink-0
                                ${modeloExportacao === "SOLAR" ? 'border-success' : 'border-input'}
                                `}>
                                {modeloExportacao === "SOLAR" && (
                                    <div className="h-2 w-2 rounded-full bg-success" />
                                )}
                            </div>

                            <div>
                                <div className="text-sm font-medium text-foreground">
                                    Modelo Solar
                                </div>

                                <div className="text-xs text-muted-foreground mt-1">
                                    Mesmo layout do "Resumo de Planejamento" da Solar. Colunas sem informação correspondente ficam em branco.
                                </div>
                            </div>
                        </button>
                    </div>
                </div>
            </Dialog>

        </div>
    );
}

export default TabelaEscalaDeRotas;

function useAdvancedTable(data) {

    const [sortConfig, setSortConfig] = useState(null);
    const [filters, setFilters] = useState({});

    const processedData = useMemo(() => {

        let items = [...data];

        Object.keys(filters).forEach((key) => {
            const selectedValues = filters[key];

            if (selectedValues?.length) {
                items = items.filter(item => {
                    const cell = getCellValue(item[key]);

                    return selectedValues.some(v =>
                        cell.toLowerCase().includes(v.toLowerCase())
                    );
                });
            }
        });

        // 🔄 ORDENAÇÃO
        if (sortConfig) {
            items.sort((a, b) => {
                const aVal = a[sortConfig.key];
                const bVal = b[sortConfig.key];

                if (aVal < bVal)
                    return sortConfig.direction === "asc" ? -1 : 1;

                if (aVal > bVal)
                    return sortConfig.direction === "asc" ? 1 : -1;

                return 0;
            });
        }

        return items;

    }, [data, sortConfig, filters]);

    const requestSort = (key, direction) => {
        setSortConfig({ key, direction });
    };

    const setColumnFilter = (key, values) => {
        setFilters(prev => ({
            ...prev,
            [key]: values
        }));
    };

    return {
        processedData,
        requestSort,
        setColumnFilter,
        sortConfig,
        filters
    };
}

function computeConflitos(rows) {
    const motoristaGrupos = new Map();
    const auxiliarGrupos = new Map();

    (rows || []).forEach(row => {
        const dia = row.dataOperacao;
        if (!dia) return;

        if (row.motoristaId) {
            const chave = `${dia}|${row.motoristaId}`;
            motoristaGrupos.set(chave, (motoristaGrupos.get(chave) || 0) + 1);
        }

        (row.auxiliares || []).forEach(aux => {
            if (!aux?.id) return;
            const chave = `${dia}|${aux.id}`;
            auxiliarGrupos.set(chave, (auxiliarGrupos.get(chave) || 0) + 1);
        });
    });

    const motoristas = new Set([...motoristaGrupos.entries()].filter(([, count]) => count > 1).map(([chave]) => chave));
    const auxiliares = new Set([...auxiliarGrupos.entries()].filter(([, count]) => count > 1).map(([chave]) => chave));

    return { motoristas, auxiliares };
}

function getCellValue(value) {
    if (value == null) return "";

    // array de objetos (auxiliares)
    if (Array.isArray(value)) {
        return value
            .map(v => v?.nome ?? v?.label ?? v?.id ?? "")
            .join(", ");
    }

    // objeto simples
    if (typeof value === "object") {
        return value.nome ?? value.label ?? value.id ?? "";
    }

    return String(value);
}
